import PropTypes from 'prop-types'
import { STATUS_COLORS } from './knowledgeMapUtils'

// Legend for the student knowledge map. Unlike the admin graph (colored by test type / difficulty),
// student nodes are colored by the student's OWN mastery status, so the legend explains those bands
// plus the prerequisite edge. In skill-graph mode it also explains the per-node DOK strip.
const STATUS_ORDER = ['MASTERED', 'IN_PROGRESS', 'STRUGGLING', 'UNVISITED']

export default function StudentGraphLegend({ skillMode = false }) {
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-xs">
      {/* Mastery status */}
      <div className="flex items-center gap-4">
        <span className="text-gray-500 dark:text-gray-400 font-medium">Mastery:</span>
        {STATUS_ORDER.map((status) => {
          const c = STATUS_COLORS[status]
          return (
            <div key={status} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-sm border"
                style={{ backgroundColor: c.bg, borderColor: c.border }}
              />
              <span className="text-gray-600 dark:text-gray-300">{c.text}</span>
            </div>
          )
        })}
      </div>

      {skillMode && (
        <>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
          {/* DOK strip explainer (skill graph only): each segment = that DOK's own mastery; the
              outlined segment is where the learner currently is. */}
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 dark:text-gray-400 font-medium">DOK levels:</span>
            <span className="flex gap-0.5">
              <span
                className="px-1 rounded text-[9px] font-medium"
                style={{
                  backgroundColor: STATUS_COLORS.MASTERED.bg,
                  border: '1px solid rgba(156,163,175,0.35)',
                  color: STATUS_COLORS.MASTERED.border
                }}
              >
                1
              </span>
              <span
                className="px-1 rounded text-[9px] font-bold"
                style={{
                  backgroundColor: STATUS_COLORS.IN_PROGRESS.bg,
                  border: `2px solid ${STATUS_COLORS.IN_PROGRESS.border}`,
                  color: STATUS_COLORS.IN_PROGRESS.border
                }}
              >
                ▶ 2
              </span>
              <span
                className="px-1 rounded text-[9px] font-medium"
                style={{
                  backgroundColor: STATUS_COLORS.UNVISITED.bg,
                  border: '1px solid rgba(156,163,175,0.35)',
                  color: STATUS_COLORS.UNVISITED.border
                }}
              >
                3
              </span>
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              each segment = that DOK&apos;s mastery; outlined = where you are now
            </span>
          </div>
        </>
      )}

      {/* Divider */}
      <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />

      {/* Edge types */}
      <div className="flex items-center gap-4">
        <span className="text-gray-500 dark:text-gray-400 font-medium">Edges:</span>
        <div className="flex items-center gap-1.5">
          <svg width="24" height="12" className="inline-block">
            <defs>
              <marker
                id="student-legend-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="4"
                markerHeight="4"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
              </marker>
            </defs>
            <line
              x1="0"
              y1="6"
              x2="20"
              y2="6"
              stroke="#94A3B8"
              strokeWidth="2"
              markerEnd="url(#student-legend-arrow)"
            />
          </svg>
          <span className="text-gray-600 dark:text-gray-300">Prerequisite</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="24" height="12" className="inline-block">
            <line
              x1="0"
              y1="6"
              x2="24"
              y2="6"
              stroke="#93C5FD"
              strokeWidth="1"
              strokeDasharray="4,3"
            />
          </svg>
          <span className="text-gray-600 dark:text-gray-300">Related</span>
        </div>
      </div>
    </div>
  )
}

StudentGraphLegend.propTypes = { skillMode: PropTypes.bool }
